// src/hooks/useSignalR.js
import { useEffect, useRef, useState, useCallback } from "react";
import signalRManager from "../services/signalRManager";

export const useSignalR = () => {
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);

  useEffect(() => {
    // Start connection using manager
    const initConnection = async () => {
      const conn = await signalRManager.startConnection();
      if (conn) {
        setConnection(conn);
        setIsConnected(signalRManager.getIsConnected());
      }
    };

    initConnection();

    // Set up event handlers using manager
    const handleMessage = (message) => {
      setMessages((prev) => [...prev, message]);
    };

    const handleUserJoined = (user) => {
      console.log("User joined:", user);
    };

    const handleUserLeft = (studentId) => {
      setOnlineUsers((prev) =>
        prev.filter((user) => user.studentId !== studentId)
      );
    };

    const handleOnlineUsersUpdated = (users) => {
      setOnlineUsers(users);
    };

    const handleTypingUsersUpdated = (users) => {
      console.log("TypingUsersUpdated event received:", users);
      setTypingUsers(users || []);
    };

    // Register handlers
    signalRManager.on("onMessage", handleMessage);
    signalRManager.on("onUserJoined", handleUserJoined);
    signalRManager.on("onUserLeft", handleUserLeft);
    signalRManager.on("onOnlineUsersUpdated", handleOnlineUsersUpdated);
    signalRManager.on("onTypingUsersUpdated", handleTypingUsersUpdated);

    // Update connection state periodically
    const interval = setInterval(() => {
      setIsConnected(signalRManager.getIsConnected());
    }, 1000);

    // Cleanup
    return () => {
      clearInterval(interval);
      // Don't disconnect here - let logout handle it
      // Remove handlers
      signalRManager.off("onMessage", handleMessage);
      signalRManager.off("onUserJoined", handleUserJoined);
      signalRManager.off("onUserLeft", handleUserLeft);
      signalRManager.off("onOnlineUsersUpdated", handleOnlineUsersUpdated);
      signalRManager.off("onTypingUsersUpdated", handleTypingUsersUpdated);
    };
  }, []);

  const sendMessage = useCallback(
    async (message) => {
      const success = await signalRManager.sendMessage(message);
      if (!success) {
        throw new Error("Failed to send message");
      }
    },
    []
  );

  const startTyping = useCallback(async () => {
    await signalRManager.startTyping();
  }, []);

  const stopTyping = useCallback(async () => {
    await signalRManager.stopTyping();
  }, []);

  const disconnect = useCallback(async () => {
    await signalRManager.disconnect();
    setIsConnected(false);
    setConnection(null);
  }, []);

  return {
    connection,
    isConnected,
    messages,
    setMessages,
    onlineUsers,
    typingUsers,
    sendMessage,
    startTyping,
    stopTyping,
    disconnect,
  };
};

export default useSignalR;
export { signalRManager };

